import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Settings, FolderTree, DollarSign, TrendingUp, Wallet, ChevronRight } from 'lucide-react';
import { ROUTES } from '@/routes';

export function SettingsProjectsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');

  const breadcrumbItems = [
    { label: 'Settings', onClick: () => navigate(ROUTES.SETTINGS) },
    { label: 'Projects', icon: <FolderTree className="h-3 w-3" /> },
  ];

  const handleBack = () => {
    navigate(ROUTES.SETTINGS);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b bg-background">
        <div className="px-6 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                className="h-8 w-8"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <FolderTree className="h-6 w-6" />
                  Projects Settings
                </h1>
              </div>
            </div>
          </div>
          <Breadcrumb items={breadcrumbItems} className="ml-12" />
          <p className="text-sm text-muted-foreground ml-12">
            Configure global project settings including general options, thresholds, and budgets
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-5xl mx-auto p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-3 max-w-md">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="thresholds">Thresholds</TabsTrigger>
              <TabsTrigger value="budgets">Budgets</TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-6">
              <div className="border rounded-lg p-8">
                <div className="text-center space-y-2">
                  <Settings className="h-12 w-12 mx-auto text-muted-foreground" />
                  <h3 className="text-lg font-semibold">General</h3>
                  <p className="text-sm text-muted-foreground">Coming soon</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="thresholds" className="space-y-6">
              <div className="border rounded-lg p-8">
                <div className="text-center space-y-2">
                  <Settings className="h-12 w-12 mx-auto text-muted-foreground" />
                  <h3 className="text-lg font-semibold">Thresholds</h3>
                  <p className="text-sm text-muted-foreground">Coming soon</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="budgets" className="space-y-6">
              <div className="border rounded-lg">
                <div className="p-6 border-b">
                  <h3 className="text-lg font-semibold">Budget Configuration</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Configure global budget tracking settings that apply to all budget types
                  </p>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {/* Budget Configuration */}
                    <button
                      onClick={() => navigate(ROUTES.SETTINGS_PROJECTS_BUDGET_CONFIGURATION)}
                      className="w-full flex items-center justify-between p-4 rounded-lg border hover:border-primary hover:bg-muted/50 transition-all group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 rounded-md bg-primary/10 text-primary">
                          <Settings className="h-5 w-5" />
                        </div>
                        <div className="text-left">
                          <div className="font-semibold group-hover:text-primary transition-colors">
                            Budget Configuration
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Configure budget tracking type (Simple, Monthly, or None) for all budget types
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    </button>

                    {/* Info Card */}
                    <div className="p-4 rounded-lg bg-info-50 dark:bg-info-950 border border-info-200 dark:border-info-900">
                      <div className="flex gap-3">
                        <div className="text-info-600 dark:text-info-400 mt-0.5">
                          <Settings className="h-5 w-5" />
                        </div>
                        <div className="flex-1 space-y-2">
                          <p className="text-sm font-medium text-info-900 dark:text-info-100">
                            Global Budget Settings
                          </p>
                          <p className="text-sm text-info-800 dark:text-info-200">
                            The budget configuration applies to all budget types (Resource, Revenue, and Cost) 
                            to ensure consistent tracking and reporting across your projects.
                          </p>
                          <div className="flex flex-wrap gap-2 mt-3">
                            <div className="flex items-center gap-2 text-xs px-2 py-1 rounded bg-info-100 dark:bg-info-900 text-info-900 dark:text-info-100">
                              <DollarSign className="h-3 w-3" />
                              Resource Budgets
                            </div>
                            <div className="flex items-center gap-2 text-xs px-2 py-1 rounded bg-info-100 dark:bg-info-900 text-info-900 dark:text-info-100">
                              <TrendingUp className="h-3 w-3" />
                              Revenue Budgets
                            </div>
                            <div className="flex items-center gap-2 text-xs px-2 py-1 rounded bg-info-100 dark:bg-info-900 text-info-900 dark:text-info-100">
                              <Wallet className="h-3 w-3" />
                              Cost Budgets
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
